import { ReactNode, UIEvent } from 'react';

export interface TableColumn<T> {
  header: string | ReactNode;
  accessor: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  skeletonRenderer?: () => ReactNode;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  skeletonRows?: number;
  emptyMessage?: ReactNode;
  className?: string;
  onScroll?: (e: UIEvent<HTMLDivElement>) => void;
  getRowKey?: (row: T) => string;
  getRowProps?: (row: T) => Record<string, string>;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  allowOverflow?: boolean;
}

export function Table<T extends { id?: string }>({
  columns,
  data,
  loading = false,
  skeletonRows = 10,
  emptyMessage,
  className = '',
  onScroll,
  getRowKey,
  getRowProps,
  onRowClick,
  isLoading = false,
  allowOverflow = false,
}: TableProps<T>) {
  const overflowClass = allowOverflow ? 'overflow-visible' : 'overflow-x-auto';
  if (loading && data.length === 0) {
    return (
      <div className={`rounded-lg bg-white shadow-md ${className}`}>
        <div className={overflowClass} onScroll={onScroll}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={`px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase ${
                      column.headerClassName || ''
                    }`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <tr key={rowIndex} className="animate-pulse">
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-6 py-4 text-sm ${column.className || 'text-gray-900'}`}
                    >
                      {column.skeletonRenderer ? (
                        column.skeletonRenderer()
                      ) : (
                        <div className="h-4 w-24 rounded bg-gray-200"></div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (data.length === 0 && emptyMessage) {
    return (
      <div className={`rounded-lg bg-white shadow-md ${className}`}>
        <div className="p-8 text-center">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg bg-white shadow-md ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/60">
          <div className="size-10 animate-spin rounded-full border-b-2 border-red-600" />
        </div>
      )}
      <div className={overflowClass} onScroll={onScroll}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase ${
                    column.headerClassName || ''
                  }`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((row) => {
              const key = getRowKey ? getRowKey(row) : row.id || JSON.stringify(row);
              return (
                <tr
                  key={key}
                  className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(row)}
                  {...getRowProps?.(row)}
                >
                  {columns.map((column, index) => (
                    <td
                      key={index}
                      className={`px-6 py-4 text-sm ${column.className || 'text-gray-900'}`}
                    >
                      {column.accessor(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
